import Layout from '../../../components/layout';
import DetailBase from '../../../components/detailBase/detailBase';
import HtmlContent from '../../../components/detailBase/htmlContent';
import { GQLAppNew, GQLBasicItem } from 'townapps-models';
import styled from 'styled-components';
import NextImage from 'next/image';
import { colors } from '../../../utils/theme';
import { Types } from '../../../utils/Types';
import { gql } from '@apollo/client';
import NotFound from '../../../components/404';
import media from 'styled-media-query';
import useScreenView from '../../../hooks/useScreenView';
import React, { useEffect, useState } from 'react';
const HorizontalCardsDetail = dynamic(() => import('../../../components/horizontalCardsDetail'), { ssr: false });
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import client from '../../../utils/apolloClient';
import dynamic from 'next/dynamic';
import useLogView from '../../../hooks/useLogView';
import Page from '../../../components/page';
import { imageDefaultShare } from '../../../utils/defaultImageShare';

const GET_NEW = gql`
  query getAppNew($ID: String) {
    appNew: getAppNew(ID: $ID) {
      id
      title
      image
      excerpt
      type
      authorImage
      htmlContent
      appId
      app {
        name
        slug
        colour
      }
      scheduledTime
      createdAt
      slug
      blurhash
      fbOgImage
      status
    }
  }
`;

const GET_NEXT_NEWS = gql`
  query getNextNews($appId: String) {
    extras: getAppNews(appId: $appId, withDrafts: false, limit: 30) {
      id
      title
      image
      excerpt
      appId
      app {
        name
        slug
      }
      scheduledTime
      createdAt
      slug
      blurhash
      fbOgImage
      type
      status
    }
  }
`;

const ItemImage = styled(NextImage)`` as typeof NextImage;

const BodyContainer = styled.div``;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 480px;

  & div img {
    object-fit: cover;
    border-radius: 8px;
  }

  ${media.lessThan('small')`
       height: 250px;
  `}
`;

const NewDetailPage = ({ appNew, preview }: { appNew: GQLBasicItem & GQLAppNew; nextNews: GQLBasicItem[] & GQLAppNew[]; preview: boolean }) => {
  useScreenView(appNew ? appNew.title : null);
  useLogView(appNew?.id, undefined, 'XploreNews');

  const router = useRouter();
  const [timeToRead, setTimeToRead] = useState(0);

  useEffect(() => {
    if (appNew && appNew.htmlContent) {
      try {
        setTimeToRead(Math.ceil(appNew.htmlContent.replace(/<[^>]*>/g, '').split(' ').length / 200));
      } catch (e) {}
    }
  }, [appNew]);

  const isFallBack = router.isFallback;

  if (isFallBack) {
    return (
      <Page>
        <DetailBase backgroundImage={'/newsHeaderBig.png'} blurHash={'LbCah@ouOGV].kocR-WDx?jaRkbI'} fallBack />
      </Page>
    );
  }

  if (!isFallBack && (!appNew || (appNew.status === 'Draft' && !preview))) {
    if (!appNew) {
      return (
        <Page>
          <DetailBase backgroundImage={'/newsHeaderBig.png'} blurHash={'LbCah@ouOGV].kocR-WDx?jaRkbI'} fallBack />
        </Page>
      );
    } else {
      return (
        <>
          <NextSeo
            title={appNew?.title}
            facebook={{
              appId: '507054450225530',
            }}
            openGraph={{
              title: appNew?.title,
              description:
                appNew && (appNew.description || (appNew as any).excerpt)
                  ? `${(appNew?.description || (appNew as any)?.excerpt).slice(0, 155)}...`
                  : '',
              url: `https://xploreapp.io${router.asPath}`,
              images: [
                {
                  url: appNew && appNew.fbOgImage ? appNew.fbOgImage : imageDefaultShare,
                  width: 1200,
                  height: 650,
                  alt: `${appNew?.title} image`,
                },
              ],
            }}
            twitter={{
              handle: '@handle',
              site: '@site',
              cardType: 'summary_large_image',
            }}
          />
          <NotFound />
        </>
      );
    }
  }

  const getBody = () => {
    return (
      <BodyContainer>
        <ImageWrapper>
          <ItemImage
            key={appNew?.id ?? 'placeholder'}
            alt={appNew?.title ?? 'Fallback'}
            src={appNew?.image ?? '/imagePlaceholder.png'}
            layout={'fill'}
          />
        </ImageWrapper>
        <p className="text-md">{timeToRead ? `~${timeToRead} minute${timeToRead > 1 ? 's' : ''} read` : ' '}</p>
        <HtmlContent html={appNew?.htmlContent} />
      </BodyContainer>
    );
  };

  return (
    <>
      <Layout>
        <DetailBase
          withDownload
          tags={
            appNew
              ? [
                  { color: colors.green, text: 'Xplore' },
                  { color: colors.black, text: Types[appNew.__typename] },
                ]
              : []
          }
          item={appNew}
          withAuthor
          Body={getBody()}
          backgroundImage={'/newsHeaderBig.png'}
          blurHash={'LbCah@ouOGV].kocR-WDx?jaRkbI'}
        />
      </Layout>

      {appNew && <HorizontalCardsDetail gqlQuery={GET_NEXT_NEWS} appID={undefined} slugApp={'xplore'} />}
    </>
  );
};

const GET_NEWS = gql`
  query getNextNews($slug: String) {
    news: getAppNews(slug: $slug, withDrafts: false) {
      id
      title
      image
      authorImage
      content
      htmlContent
      excerpt
      appId
      slug
      blurhash
      fbOgImage
      app {
        sponsoredLogo
        slug
        users {
          email
        }
      firstSponsor
      secondSponsor
      thirdSponsor
      fourthSponsor
      fifthSponsor
      sixthSponsor
      }
      status
      extraApps
      scheduledTime
      createdAt
    }
  }
`;

export async function getStaticPaths() {
  const apolloClient = client;
  const paths = [];
  const newsRes = await apolloClient.query<{ news: GQLAppNew[] }>({
    query: GET_NEWS,
    variables: { appId: undefined },
  });
  const news = newsRes.data.news;
  if (news && news.length)
    for (const appNew of news) {
      paths.push({ params: { slug: 'xplore', id: appNew.slug } });
    }

  return {
    paths: paths,
    fallback: true,
  };
}

export async function getStaticProps(ctx) {
  const id = ctx.params.id;
  const apolloClient = client;
  const res = await apolloClient.query<{ appNew: GQLAppNew }>({ query: GET_NEW, variables: { ID: id } });

  if (ctx.preview) {
    return {
      props: { appNew: res.data.appNew, preview: true },
    };
  } else {
    return {
      props: { appNew: res.data.appNew },
      revalidate: 900,
    };
  }
}

export default NewDetailPage;
